import { type ReactNode } from 'react';
import { PersonOutlined as PersonOutlinedIcon, School as SchoolIcon, Church as ChurchIcon, Forest as ForestIcon, Savings as SavingsIcon, History as HistoryIcon, Apartment as CommunityIcon } from '@mui/icons-material';
import {
	generateRecommendationUploadLink,
	generateUnsubscribeLinkCallable,
	hashRecommendationPin,
	resolveRecommendationUpload,
	resolveUnsubscribeToken,
	verifyRecommendationUploadPin,
} from './firebase';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { ApplicationType } from './collections';
import type { ApplicationTypeValue } from './collections';
import type { SiteConfig } from '../../types';
import fdl from '../../assets/images/FDL.png';
import lightModeHero from '../../assets/images/woodFDL.jpg';
import SSLogo from '../../assets/images/SSL.png';
import darkModeHero from '../../assets/images/night-site-FDL.png';
import parkerFundHeader from '../../assets/images/PFHeader.png';
import accessDeniedAnimation from '../../assets/lotties/AccessDenied.json';
import underConstructionAnimation from '../../assets/lotties/UnderConstruction.json';
import contentNotFoundAnimation from '../../assets/lotties/NotFound.json';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface Sender {
    id: string;
    name: string;
    email: string;
}

export const senders: Sender[] = [
    { id: '1', name: 'Parker Memorial Fund', email: 'noreply@revparkermemorialfund.org' },
    { id: '2', name: 'Parker Memorial Fund', email: 'applications@revparkermemorialfund.org' },
    { id: '3', name: 'Parker Memorial Fund', email: 'webmaster@revparkermemorialfund.org' },
];

export const brand = {
	theOrganizationName: 'The Reverend Robert B. Parker Memorial Fund',
	organizationName: 'Reverend Robert B. Parker Memorial Fund',
	organizationShortName: 'Parker Memorial Fund',
    internalName: 'Parker Fund',
	boardName: 'The Parker Memorial Fund Board',
	tagline: 'Scholarships for Scouts to promote leadership, patriotism, and self-reliance.',
	organizationAddress: 'Spirit Of Adventure Council<br />Attn: Rev. Robert B. Parker Memorial Fund<br />2 Tower Office Park<br />Woburn, MA 01801',
	organizationEstablished: 'Marblehead, Massachusetts | Established 1939',
	contactEmail: 'applications@revparkermemorialfund.org',
    helpEmail: 'applications@revparkermemorialfund.org',
	url: 'https://revparkermemorialfund.org',
	/** Apple App Store numeric id (ASC). Used for Smart App Banner. */
	appleAppId: '6821174234',
    domain: 'revparkermemorialfund.org',
    broughtToYouBy: 'Spirit of Adventure Council',
    localCouncilName: 'Spirit of Adventure Council',
    localCouncilShortName: 'SoAC',
    localCouncilWebsiteUrl: 'https://scoutspirit.org',
    emailDomain: 'revparkermemorialfund.org',

	metaAuthor: 'Rev. Robert B. Parker Memorial Fund',
	keywords: 'Scouting scholarships, Boy Scout scholarships, Scoutcraft scholarships, Marblehead Scouts scholarship, Youth scholarships in Massachusetts, Reverend Robert B. Parker Memorial Fund, Scholarships for community service, Local council Scouting scholarships, Scholarships for Scouts, Patriotism and youth leadership scholarships, Scholarships for Scouts in Marblehead MA, Boy Scout college scholarships in Massachusetts, Youth leadership scholarships for Scouts, How to apply for Scouting scholarships, Scholarships for youth in Spirit of Adventure Council, Local council scholarships for youth development, Scholarship fund for Scouts with financial need, Financial aid for Scouts attending college, Scholarships for Eagle Scouts and Boy Scouts, College scholarships for Marblehead Scouts, Endowed scholarships for Scouts, Scholarship funds for North Shore Scouts, Scout endowment fund, General Electric stock endowed scholarship, Scholarships for patriotism and self-reliance, Courage and leadership youth scholarships, Scoutcraft training scholarships for Scouts, Camp scholarships for Boy Scouts, Scholarship fund for Scoutcraft and community service',
	shortDescription: 'Offering scholarships for Scouts to promote leadership, patriotism, and self-reliance. Scholarships available for Scouts in the Spirit of Adventure Council and beyond.',
	metaDescription: 'The Reverend Robert B. Parker Memorial Fund supports Scouts by promoting leadership, patriotism, and self-reliance. Established in 1939, it has awarded over $1.2 million in scholarships and Scouting programs to help Scouts pursue education and improve local camps.',
	ogImage: 'PFHeader1200.png',
	contactTelephone: '+1-978-578-0234',
	contactType: 'Chairman of the Fund',
	favIcon: 'favicon-32x32.png',
	appleTouchIcon: 'apple-touch-icon-v3.png',

	/** Default MUI primary accent (Venturing Green). PWA/Chromium chrome uses surface colors instead. */
	themeColor: process.env.REACT_APP_THEME_COLOR,
	lightBackgroundColor: process.env.REACT_APP_PRELOAD_BG_LIGHT,
	darkBackgroundColor: process.env.REACT_APP_PRELOAD_BG_DARK,
} as const;

export const Assets = {
    logo: fdl,
    sslLogo: SSLogo,
    heroLM: lightModeHero,
    heroDM: darkModeHero,
    header: parkerFundHeader,
    accessDeniedLottie: accessDeniedAnimation,
    underConstructionLottie: underConstructionAnimation,
    notFoundLottie: contentNotFoundAnimation,
} as const;

interface LetterOfRecommendation {
	name: string;
	purpose: string;
	icon: ReactNode;
	requiredBy: readonly ApplicationTypeValue[];
}

export const LettersOfRecommendation: Record<string, LetterOfRecommendation> = {
	religiousRecommendationLetter: {
		name: 'Religious Letter of Recommendation',
		purpose: 'attest the applicant takes an active role in their faith and religious community',
		icon: <ChurchIcon />,
		requiredBy: [ApplicationType.newApplication],
	},
	academicRecommendationLetter: {
		name: 'Academic Letter of Recommendation',
		purpose: 'attest the applicant excels in their academic pursuits',
		icon: <SchoolIcon />,
		requiredBy: [ApplicationType.newApplication, ApplicationType.returningGrant],
	},
	scoutRecommendationLetter: {
		name: "Scouting Letter of Recommendation",
		purpose: "attest the scout excels in their scouting community and still maintains an active role",
		icon: <ForestIcon />,
		requiredBy: [ApplicationType.newApplication],
	},
};

interface AttachmentField {
	key: string;
	label: string;
	icon: ReactNode;
	requiredBy: readonly ApplicationTypeValue[];
}

export const attachmentFields: AttachmentField[] = [
	{
		key: 'applicantPersonalLetter',
		label: 'Personal Letter',
		icon: <PersonOutlinedIcon />,
		requiredBy: [ApplicationType.newApplication, ApplicationType.returningGrant, ApplicationType.scholarship],
	},
	...Object.keys(LettersOfRecommendation).map((key) => ({
		label: LettersOfRecommendation[key].name,
		key: key,
		icon: LettersOfRecommendation[key].icon,
		requiredBy: LettersOfRecommendation[key].requiredBy,
	})),
	{
		key: 'studentAidReport',
		label: 'Student Aid Report',
		icon: <SavingsIcon />,
		requiredBy: [ApplicationType.newApplication, ApplicationType.returningGrant],
	},
	{
		key: 'acceptanceLetter',
		label: 'School Acceptance Letter',
		icon: <CommunityIcon />,
		requiredBy: [ApplicationType.newApplication],
	},
	{
		key: 'academicTranscript',
		label: 'Most Recent Academic Transcript',
		icon: <HistoryIcon />,
		requiredBy: [ApplicationType.newApplication, ApplicationType.returningGrant, ApplicationType.scholarship],
	},
];

export const Pages = {
	caRoot: 'rootCatchAll',
	caApps: 'applicationsCatchAll',
	caMembers: 'membersCatchAll',
	caApplicants: 'applicantsCatchAll',
	notFound: '404',
	caAccessDenied: 'accessDenied',
	adminAccessDenied: 'adminAccessDenied',
	root: 'root',
	logout: 'logout',
	redirect: 'redirect',
	home: 'home',
	applyHome: 'applyHome',
	createApplication: 'createApplication',
	updateApplication: 'updateApplication',
	registerApplicant: 'registerApplicant',
	registerMember: 'registerMember',
	login: 'login',
	changePassword: 'changePassword',
	privacy: 'privacy',
	terms: 'terms',
	mobileApp: 'mobileApp',
	allApplicants: 'allApplicants',
	viewApplicant: 'viewApplicant',
	editApplicant: 'editApplicant',
	newApplicant: 'newApplicant',
	memberDash: 'memberDash',
	contactCenter: 'contactCenter',
	chatInbox: 'chatInbox',
	allMembers: 'allMembers',
	siteSettings: 'siteSettings',
	financeCenter: 'financeCenter',
	cycleExpenses: 'cycleExpenses',
	viewMember: 'viewMember',
	editMember: 'editMember',
	newMember: 'newMember',
	reviewApp: 'reviewApp',
	allApps: 'allApps',
	applicationYear: 'applicationYear',
	allAppsInYear: 'allAppsInYear',
	newAppsInYear: 'newAppsInYear',
	returningAppsInYear: 'returningAppsInYear',
	scholarshipAppsInYear: 'scholarshipAppsInYear',
	newApps: 'newApps',
	returningApps: 'returningApps',
	scholarshipApps: 'scholarshipApps',
	completedApps: 'completedApps',
	completedAppsInYear: 'completedAppsInYear',
	eligibleAppsInYear: 'eligibleAppsInYear',
	invitedAppsInYear: 'invitedAppsInYear',
	awardedAppsInYear: 'awardedAppsInYear',
	deletedAppsInYear: 'deletedAppsInYear',
	rejectedAppsInYear: 'rejectedAppsInYear',
	incompleteAppsInYear: 'incompleteAppsInYear',
	eligibleApps: 'eligibleApps',
	invitedApps: 'invitedApps',
	awardedApps: 'awardedApps',
	rejectedApps: 'rejectedApps',
	deletedApps: 'deletedApps',
	incompleteApps: 'incompleteApps',
	viewApp: 'viewApp',
	exportApp: 'exportApp',
	archives: 'archives',
	unsubscribe: 'unsub',
	requests: 'requests',
	allRequests: 'allRequests',
	editRequest: 'editRequest',
	newRequest: 'newRequest',
	interviews: 'interviews',
	scheduling: 'scheduling',
	interviewCallSession: 'interviewCallSession',
	waitingRoom: 'waitingRoom',
	interviewRoom: 'interviewRoom',
	deliberationRoom: 'deliberationRoom',
	interviewDash: 'interviewDash',
	rsvp: 'rsvp',
	aiReview: 'aiReview',
	inbox: 'inbox',
	viewEmail: 'viewEmail',
} as const;

export type PageKey = keyof typeof Pages;

interface AlertMessage {
	message: string;
	type: 'success' | 'error' | 'warning' | 'info';
}

export const AlertMessages: Record<string, Record<string, AlertMessage>> = {
	general: {
		incomplete: { message: 'One or more required fields are incomplete!', type: 'warning' },
		unauthorized: { message: 'You cannot access this at this time.', type: 'error' },
		revoked: { message: 'Your access to the system is revoked.', type: 'error' },
		maintenance: { message: 'The system is down for maintenance. Please try again later.', type: 'error' },
		success: { message: 'Operation successfully completed.', type: 'success' },
	},
	login: {
		success: { message: 'You have successfully logged in!', type: 'success' },
		failed: { message: 'Error logging you in!', type: 'error' },
	},
	register: {
		success: { message: 'You have successfully registered!', type: 'success' },
		exists: { message: 'This email address is already registered.  Try logging in instead!', type: 'info' },
		notmatching: { message: 'Your passwords do not match!', type: 'error' },
		failed: { message: 'Error registering for an account!', type: 'error' },
		password: { message: 'Passwords must be 8 or more characters consisting of at least 1 uppercase, 1 lowercase, 1 number, and 1 symbol characters.', type: 'error' },
	},
	upload: {
		success: { message: 'Your upload was successful!', type: 'success' },
		failed: { message: 'Your upload has failed.', type: 'error' },
		incompatible: { message: 'Your file type is incompatible!', type: 'error' },
		unavailable: { message: 'Access to this file is unavailable.', type: 'warning' },
		missing: { message: 'The file could not be opened.', type: 'error' },
		deleted: { message: 'Your file was deleted successfully.', type: 'success' },
		type: { message: 'Files must be of PDF type only!', type: 'error' },
		size: { message: 'File size exceeds the limit. Please select a file smaller than 25MB.', type: 'error' },
		requested: { message: 'Your request was sent successfully!', type: 'success' },
	},
	settings: {
		success: { message: 'Your settings were updated successfully.', type: 'success' },
		failed: { message: 'The setting could not be updated.', type: 'error' },
	},
	application: {
		saved: { message: 'Your application progress has been saved!', type: 'success' },
		failed: { message: 'Failed to save your application progress.', type: 'error' },
		updated: { message: 'Step saved successfully!', type: 'success' },
		enabled: { message: 'This application is fully modifiable.', type: 'info' },
		disabled: { message: 'You cannot make changes to this application.', type: 'info' },
		submitted: { message: 'Your application was submitted successfully!', type: 'success' },
		error: { message: 'Error saving application!', type: 'error' },
		completed: { message: 'Your application is now complete!', type: 'success' },
		incomplete: { message: 'Your application is incomplete', type: 'warning' },
		deleted: { message: 'Your application was deleted.', type: 'success' },
	},
	applicant: {
		updated: { message: 'Applicant data updated successfully!', type: 'success' },
		failed: { message: 'Applicant update failed.', type: 'error' },
	},
	validation: {
		fields: { message: 'Please complete all required fields!', type: 'warning' },
		missing: { message: 'You must add at least one!', type: 'warning' },
	},
};

const getDomain = (): string => globalThis.location.origin;
const ENV = process.env.REACT_APP_environment;
export const domain: string = ENV === 'development' ? 'https://localhost:3000' : getDomain();

export const generate6DigitNumber = (): string => {
	const randomNumber = Math.floor(Math.random() * 1000000);
	return String(randomNumber).padStart(6, '0');
};

export const generateSecurePin = async (pin: string): Promise<string> => {
	const result = await hashRecommendationPin({ pin });
	const data = result.data as { pinCode?: string };
	if (!data?.pinCode) throw new Error('Failed to hash recommendation pin.');
	return data.pinCode;
};

export const unsubscribeLink = async (id: string | undefined, _config?: SiteConfig | null): Promise<string> => {
	if (!id) return '#';
	const result = await generateUnsubscribeLinkCallable({ userId: id });
	const data = result.data as { link?: string };
	return data?.link || '#';
};

export const generateUploadLink = async (requestID: string): Promise<string> => {
	const result = await generateRecommendationUploadLink({ requestId: requestID });
	const data = result.data as { link?: string };
	if (!data?.link) throw new Error('Failed to generate upload link.');
	return data.link;
};

interface ValidateResult {
	result: boolean;
	id?: string;
	error?: string;
	request?: Record<string, unknown>;
}

export const validateRequest = async (token: string): Promise<ValidateResult> => {
	try {
		const result = await resolveRecommendationUpload({ token });
		const data = result.data as Record<string, unknown>;
		return { result: true, id: String(data.id), request: data };
	} catch (error) {
		console.error('Error validating upload link:', error);
		return { result: false, error: (error as Error).message };
	}
};

/** @deprecated Pin validation is server-side; prefer verifyRecommendationUploadPin(token, pin). */
export const validatePin = async (_pin: string): Promise<boolean> => false;

/**
 * Verify pin against a magic-link token (server-side).
 * Legacy signature (plainPin, storedSecurePin) is no longer supported without a token.
 */
export const verifyUploadPin = async (plainPin: string, tokenOrStored?: string | null): Promise<boolean> => {
	const entered = plainPin.trim();
	if (!entered || !tokenOrStored) return false;
	// New path: second arg is the magic-link token (contains ':' when base64-decoded as id:hash,
	// but we detect by attempting the callable).
	try {
		await verifyRecommendationUploadPin({ token: tokenOrStored, pin: entered });
		return true;
	} catch {
		return false;
	}
};

export const validateLink = async (encID: string): Promise<ValidateResult> => {
	try {
		const result = await resolveUnsubscribeToken({ token: encID });
		const data = result.data as { result?: boolean; id?: string };
		return { result: Boolean(data?.result), id: data?.id };
	} catch (error) {
		console.error('Error validating unsubscribe link:', error);
		return { result: false, error: (error as Error).message };
	}
};

export const capitalize = (s: string): string => {
	if (typeof s !== 'string' || !s) return '';
	return s.charAt(0).toUpperCase() + s.slice(1);
};

export const convertPDFBlobToImages = async (blob: Blob): Promise<string[]> => {
	const objectURL = URL.createObjectURL(blob);
	const pdf = await pdfjsLib.getDocument(objectURL).promise;
	const pageCount = pdf.numPages;
	const imageURLs: string[] = [];

	for (let i = 1; i <= pageCount; i++) {
		const page = await pdf.getPage(i);
		const viewport = page.getViewport({ scale: 2 });

		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d')!;
		canvas.height = viewport.height;
		canvas.width = viewport.width;

		await page.render({ canvasContext: context, viewport }).promise;

		const dataURL = canvas.toDataURL();
		imageURLs.push(dataURL);
	}

	URL.revokeObjectURL(objectURL);
	return imageURLs;
};

export const createBlobUrl = (blob: Blob): string => {
	return URL.createObjectURL(blob);
};

export const revokeBlobUrl = (url: string): void => {
	URL.revokeObjectURL(url);
};

export const emailHeader: string = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>Welcome to ${brand.organizationShortName}</title></head><body style="width: 100%; margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif;"><header><table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f4f4f4; padding: 20px; margin-bottom: 20px; font-family: Arial, Helvetica, sans-serif;"><tr><td align="center"><table cellpadding="0" cellspacing="0" border="0" style="background-color: #ffffff; padding: 20px; border-radius: 5px;"><tr><td align="center" style="font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #333333; padding: 10px;"><h1 style="font-size: 24px; color: #006B3F; margin: 0;">${brand.theOrganizationName}</h1><p style="font-size: 16px; color: #666666; margin: 5px 0 20px;">${brand.tagline}</p></td></tr><tr><td style="border-top: 2px solid #006B3F; padding-top: 10px;"><p style="font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #666666; text-align: center;">${brand.organizationEstablished}</p></td></tr></table></td></tr></table></header>`;

export const emailFooter = (unsubLink: string): string => {
	return `<footer><table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f4f4f4; padding: 20px; margin-top: 20px; font-family: Arial, Helvetica, sans-serif;"><tr><td style="font-family: Arial, Helvetica, sans-serif; font-size: 10px; color: #666666; padding: 20px 0 10px; text-align: center;"><p>You are receiving this email because you opted in at our website.<br /><a href=${unsubLink} style="color: #006B3F;">Unsubscribe</a> | <a href="#" style="color: #006B3F;">Privacy Policy</a></p><p>© ${new Date().getFullYear()} ${
		brand.theOrganizationName
	}. All rights reserved.</p></td></tr></table></footer></body></html>`;
};

export const staticEmailFooter: string = `<footer><table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f4f4f4; padding: 20px; margin-top: 20px; font-family: Arial, Helvetica, sans-serif;"><tr><td style="font-family: Arial, Helvetica, sans-serif; font-size: 10px; color: #666666; padding: 20px 0 10px; text-align: center;"><p>You are receiving this email because you opted in at our website.<br /><a href="#" style="color: #006B3F;">Privacy Policy</a></p><p>© ${new Date().getFullYear()} ${
	brand.theOrganizationName
}. All rights reserved.</p></td></tr></table></footer></body></html>`;
